import React, { useEffect, useMemo } from 'react'
import TagManager from 'react-gtm-module'
import { useNavigate } from 'react-router-dom'

import { useDashboardPermissions } from '@components'
import {
  Heading,
  Icon,
  IconButton,
  Loader,
  ModalBox,
  useFocus,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { ChevronLeftIcon, CloseIcon } from '@fourthwall/icons'
import type { PaymentMethodItem } from '@modules/Dashboard/modules/PaymentMethods/PaymentMethods.types'
import { routing } from '@utils/routing'

import type { SubscriptionsAnalyticsSource } from '../../Subscriptions.types'
import { useSubscription } from '../../hooks/useSubscription'
import * as S from './AddSubscriptionModal.styled'
import type { Stage } from './AddSubscriptionModal.types'
import { IntroStage } from './components/IntroStage/IntroStage'
import { PaymentStage } from './components/PaymentStage/PaymentStage'
import { SelectPlanStage } from './components/SelectPlanStage/SelectPlanStage'
import { Slider } from './components/Slider/Slider'
import { SuccessStage } from './components/SuccessStage/SuccessStage'
import { usePlans } from './hooks/usePlans'
import { useProcessing } from './hooks/useProcessing'
import { useStage } from './hooks/useStage'
import { useCreateSubscriptionPlanMutation } from './queries/useCreateSubscriptionPlanMutation'

export type AddSubscriptionModalProps = {
  title?: string
  initialPlanId?: string
  initialStage?: Stage
  showSuccessScreen?: boolean
  source?: SubscriptionsAnalyticsSource
  onSuccess?: () => void
}

export const AddSubscriptionModal = ({
  title = 'Upgrade to Fourthwall Pro',
  initialPlanId,
  initialStage,
  showSuccessScreen = true,
  source,
  onSuccess,
}: AddSubscriptionModalProps) => {
  const { close, open } = useModalContext()
  const media = useMediaQueryContext()
  const navigate = useNavigate()
  const { invalidateSubscriptionData } = useSubscription()
  const { createSubscriptionPlanMutationMutation } = useCreateSubscriptionPlanMutation()
  const { stage, canGoToPreviousStage, goToPreviousStage, goToNextStage } = useStage(initialStage)
  const { plans, selectedPlan, setSelectedPlan } = usePlans(initialPlanId)
  const [closeButtonRef, setCloseButtonFocus] = useFocus()
  const { hasPermission } = useDashboardPermissions()
  const { isProcessing, setIsProcessing } = useProcessing({
    onSuccess: goToNextStage,
  })
  const canUserUpgradeSubscription = hasPermission('subscriptions.edit')

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: { event: 'subscription_modal_opened', source },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (stage === 'SELECT_PLAN') {
      TagManager.dataLayer({
        dataLayer: { event: 'subscription_plan_selection_opened', source },
      })
    }

    if (stage === 'PAYMENT') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'subscription_checkout_started',
          source,
          subscription_plan: selectedPlan?.analytics,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  const showSlider = useMemo(() => {
    if (media.tablet) {
      return true
    }

    return stage === 'INTRO'
  }, [media.tablet, stage])

  const handleAddPaymentMethodClose = () => {
    open(AddSubscriptionModal, {
      title,
      initialPlanId: selectedPlan?.id,
      initialStage: 'PAYMENT',
      showSuccessScreen,
      onSuccess,
    })
  }

  const handlePaymentSuccess = async () => {
    await createSubscriptionPlanMutationMutation.mutateAsync([{ planId: selectedPlan?.id! }])

    invalidateSubscriptionData()

    if (!showSuccessScreen) {
      close()
      onSuccess?.()

      return
    }

    setIsProcessing(true)
  }

  const handlePaymentSubmit = (paymentMethod?: PaymentMethodItem) => {
    let paymentMethodInfo = 'unknown'

    if (paymentMethod) {
      if (paymentMethod.type === 'CARD') {
        paymentMethodInfo = 'credit_card'
      } else if (paymentMethod.type === 'PAYPAL') {
        paymentMethodInfo = 'paypal'
      }
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'subscription_payment_started',
        source,
        payment_method: paymentMethodInfo,
      },
    })
  }

  const handleClaimFreeDomain = () => {
    navigate(routing.settings.domain.self)
    close()
    onSuccess?.()
  }

  const handleOrderSamples = () => {
    navigate(routing.settings.samples.order)
    close()
    onSuccess?.()
  }

  useEffect(() => {
    setCloseButtonFocus()
  }, []) // make sure it's an empty array

  const getTitle = () => {
    if (!canUserUpgradeSubscription) {
      return 'Contact the shop owner to upgrade to Fourthwall Pro'
    }

    if (stage === 'SUCCESS') {
      return 'Welcome to Fourthwall Pro!'
    }

    return title
  }

  const renderContent = () => {
    if (isProcessing) {
      return (
        <S.CenterContent>
          <S.LoaderWrapper>
            <Loader size="large" />
          </S.LoaderWrapper>
          <Heading type="h3" weight="semibold">
            Processing your payment...
          </Heading>
          This may take a minute.
        </S.CenterContent>
      )
    }

    return (
      <S.MainContent>
        <S.HeaderContainer>
          {canGoToPreviousStage && (
            <IconButton
              icon={ChevronLeftIcon}
              size="micro"
              variant="semi-transparent"
              onClick={goToPreviousStage}
            />
          )}
          <Heading type="h3" weight="semibold">
            {getTitle()}
          </Heading>
        </S.HeaderContainer>

        {stage === 'INTRO' && <IntroStage onNext={goToNextStage} onClose={close} />}

        {stage === 'SELECT_PLAN' && (
          <SelectPlanStage
            plans={plans}
            selectedPlanId={selectedPlan?.id!}
            onPlanSelect={setSelectedPlan}
            onContinue={goToNextStage}
          />
        )}

        {stage === 'PAYMENT' && (
          <PaymentStage
            total={selectedPlan?.totalPrice!}
            isSubmitting={createSubscriptionPlanMutationMutation.isLoading}
            onSuccess={handlePaymentSuccess}
            onAddPaymentMethodClose={handleAddPaymentMethodClose}
            onSubmitCallback={handlePaymentSubmit}
          />
        )}

        {stage === 'SUCCESS' && (
          <SuccessStage
            onOrderSamples={handleOrderSamples}
            onClaimFreeDomain={handleClaimFreeDomain}
            onClose={close}
          />
        )}
      </S.MainContent>
    )
  }

  return (
    <ModalBox
      width={media.tablet ? '930px' : undefined}
      size={!media.tablet ? 'fullScreen' : undefined}
      fullWidth={!media.tablet}
      onClose={close}
      withoutPadding
      withoutCloseButton
    >
      <S.Container>
        <S.CloseButton
          aria-label="Close"
          type="button"
          ref={closeButtonRef}
          variant={!media.tablet && stage !== 'INTRO' ? 'dark' : 'light'}
          onClick={close}
        >
          <Icon component={CloseIcon} height={media.tablet ? 16 : 12} />
        </S.CloseButton>

        {renderContent()}

        {showSlider && (
          <S.SidebarContent>
            <Slider />
          </S.SidebarContent>
        )}
      </S.Container>
    </ModalBox>
  )
}
